import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  PropertyCandidate,
  CandidateConfidence,
} from "../../../redux/slices/propertyCandidates";

interface CandidateReviewPanelProps {
  candidates: PropertyCandidate[];
  pending: number;
  approved: number;
  total: number;
  confirming: boolean;
  onApprove: (candidateId: number) => void;
  onReject: (candidateId: number) => void;
  onBulkApproveHigh: () => void;
  onConfirm: () => void;
  selectedCandidateId: number | null;
  bufferDistance: 25 | 50;
  onChangeBuffer: (d: 25 | 50) => void;
  skipForever: boolean;
  onSkipForeverChange: (value: boolean) => void;
}

const confidenceLabel: Record<CandidateConfidence, string> = {
  HIGH: "Korkea",
  MEDIUM: "Keskitaso",
  LOW: "Matala",
};

const confidenceColor: Record<
  CandidateConfidence,
  "success" | "warning" | "default"
> = {
  HIGH: "success",
  MEDIUM: "warning",
  LOW: "default",
};

const statusLabel: Record<string, string> = {
  PENDING_REVIEW: "Odottaa",
  APPROVED: "Hyväksytty",
  REJECTED: "Hylätty",
};

const CandidateReviewPanel: React.FC<CandidateReviewPanelProps> = ({
  candidates,
  pending,
  approved,
  total,
  confirming,
  onApprove,
  onReject,
  onBulkApproveHigh,
  onConfirm,
  selectedCandidateId,
  bufferDistance,
  onChangeBuffer,
  skipForever,
  onSkipForeverChange,
}) => {
  const highCount = candidates.filter(
    (c) => c.confidence === "HIGH" && c.status === "PENDING_REVIEW"
  ).length;

  const reviewed = total - pending;

  return (
    <Paper
      elevation={4}
      sx={{
        position: "absolute",
        top: 12,
        right: 12,
        width: 320,
        maxHeight: "calc(100% - 24px)",
        display: "flex",
        flexDirection: "column",
        zIndex: 10,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, bgcolor: "primary.main", color: "white" }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Kiinteistöehdotukset
        </Typography>
        <Typography variant="caption">
          {reviewed} / {total} tarkistettu
        </Typography>
      </Box>

      {/* Buffer distance toggle */}
      <Box
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Puskurivyöhyke:
        </Typography>
        <Button
          size="small"
          variant={bufferDistance === 25 ? "contained" : "outlined"}
          onClick={() => onChangeBuffer(25)}
          sx={{ minWidth: 48, px: 1, py: 0.25 }}
        >
          25m
        </Button>
      </Box>

      {/* Bulk action */}
      {highCount > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            size="small"
            variant="outlined"
            color="success"
            fullWidth
            onClick={onBulkApproveHigh}
          >
            Hyväksy kaikki korkean varmuuden ({highCount})
          </Button>
        </Box>
      )}

      {/* Candidate list */}
      <List dense sx={{ flex: 1, overflowY: "auto", py: 0 }} disablePadding>
        {candidates.map((candidate, idx) => (
          <React.Fragment key={candidate.id}>
            {idx > 0 && <Divider component="li" />}
            <ListItem
              alignItems="flex-start"
              sx={{
                bgcolor:
                  selectedCandidateId === candidate.id
                    ? "action.selected"
                    : candidate.status === "APPROVED"
                    ? "success.50"
                    : candidate.status === "REJECTED"
                    ? "action.disabledBackground"
                    : "background.paper",
                opacity: candidate.status === "REJECTED" ? 0.55 : 1,
                px: 1.5,
                py: 1,
              }}
              secondaryAction={
                candidate.status === "PENDING_REVIEW" ? (
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Hyväksy">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => onApprove(candidate.id)}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Hylkää">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onReject(candidate.id)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {statusLabel[candidate.status]}
                  </Typography>
                )
              }
            >
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {candidate.name || candidate.externalParcelId}
                  </Typography>
                }
                secondary={
                  <Stack direction="column" spacing={0.25} mt={0.25}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {candidate.externalParcelId}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip
                        label={confidenceLabel[candidate.confidence]}
                        color={confidenceColor[candidate.confidence]}
                        size="small"
                        sx={{ height: 18, fontSize: 10 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(candidate.distanceFromRoadM)}m
                      </Typography>
                    </Stack>
                  </Stack>
                }
                sx={{ pr: 7 }}
              />
            </ListItem>
          </React.Fragment>
        ))}
      </List>

      {/* Footer */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={1}
        >
          Hyväksytty: {approved} · Odottaa: {pending}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={skipForever}
              onChange={(e) => onSkipForeverChange(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption">
              Ohita kiinteistöehdotukset pysyvästi
            </Typography>
          }
          sx={{ mb: 1 }}
        />
        <Button
          variant="contained"
          fullWidth
          onClick={onConfirm}
          disabled={confirming}
          color={skipForever ? "warning" : "primary"}
          startIcon={
            confirming ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {skipForever ? "Ohita pysyvästi" : "Vahvista valinta"}
        </Button>
      </Box>
    </Paper>
  );
};

export default CandidateReviewPanel;
